import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { MobileNavigation } from '../components/MobileNavigation';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Users, Calendar, CheckCircle, CreditCard, Clock, TrendUp, CurrencyDollar, Warning, Bell, ChartLine, UserGear, MapPin, Gear, FileText, ShieldCheck, Download, GearSix, LockKey, SignOut, UserList, FirstAid, Scroll, Trash, Basketball } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getApiBaseUrl } from '../utils/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('admin');
  const [todaySessions, setTodaySessions] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [financialSummary, setFinancialSummary] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [attendanceData, setAttendanceData] = useState(null);
  const [waiverSummary, setWaiverSummary] = useState({ players: [], summary: { signed: 0, expired: 0, missing: 0 } });
  const [cleaningUp, setCleaningUp] = useState(false);
  
  const handleCleanupDemoData = async () => {
    const confirmed = window.confirm(
      '⚠️ PRODUCTION CLEANUP\n\n' +
      'This will permanently delete:\n' +
      '• All sessions\n' +
      '• All player registrations\n' +
      '• All players\n' +
      '• All check-ins\n' +
      '• All credits\n\n' +
      'User accounts will be preserved.\n\n' +
      'Are you sure you want to proceed?'
    );
    
    if (!confirmed) return;
    
    const doubleConfirm = window.confirm(
      '🚨 FINAL CONFIRMATION\n\n' +
      'This action CANNOT be undone.\n\n' +
      'Type OK to confirm cleanup.'
    );
    
    if (!doubleConfirm) return;
    
    setCleaningUp(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/cleanup/archive-demo-data`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Cleanup complete! Deleted: ${result.details.sessions_deleted} sessions, ${result.details.players_deleted} players, ${result.details.registrations_deleted} registrations`);
        // Reload dashboard data
        loadDashboardData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Cleanup failed');
      }
    } catch (error) {
      console.error('Cleanup error:', error);
      toast.error('Cleanup failed');
    } finally {
      setCleaningUp(false);
    }
  };
  
  useEffect(() => {
    loadDashboardData();
  }, []);
  
  const loadDashboardData = async () => {
    try {
      // Get user info
      const userResponse = await fetch(`${getApiBaseUrl()}/api/auth/me`, {
        credentials: 'include'
      });
      if (userResponse.ok) {
        const userData = await userResponse.json();
        setUser(userData);
      }
      
      // Get dashboard stats
      const statsResponse = await fetch(`${getApiBaseUrl()}/api/dashboard/stats`, {
        credentials: 'include'
      });
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }
      
      // Get today's sessions - public endpoint, no credentials needed
      const today = new Date().toISOString().split('T')[0];
      const sessionsResponse = await fetch(`${getApiBaseUrl()}/api/sessions`);
      if (sessionsResponse.ok) {
        const sessionsData = await sessionsResponse.json();
        const todaysSessions = sessionsData.filter(session => session.date === today);
        setTodaySessions(todaysSessions);
      }
      
      // Get admin-only data
      const alertsResponse = await fetch(`${getApiBaseUrl()}/api/admin/alerts`, {
        credentials: 'include'
      });
      if (alertsResponse.ok) {
        const alertsData = await alertsResponse.json();
        setAlerts(alertsData.alerts || []);
      }
      
      // Get attendance analytics
      const attendanceResponse = await fetch(`${getApiBaseUrl()}/api/admin/analytics/attendance`, {
        credentials: 'include'
      });
      if (attendanceResponse.ok) {
        const attendanceData = await attendanceResponse.json();
        setAttendanceData(attendanceData);
      }
      
      // Get recent transactions (admin only)
      const transactionsResponse = await fetch(`${getApiBaseUrl()}/api/admin/financial/transactions?limit=5`, {
        credentials: 'include'
      });
      if (transactionsResponse.ok) {
        const transactionsData = await transactionsResponse.json();
        setRecentTransactions(transactionsData);
      }
      
      const summaryResponse = await fetch(`${getApiBaseUrl()}/api/admin/financial/summary`, {
        credentials: 'include'
      });
      if (summaryResponse.ok) {
        const summaryData = await summaryResponse.json();
        setFinancialSummary(summaryData);
      }
      
      // Get waiver summary (admin only)
      const waiverResponse = await fetch(`${getApiBaseUrl()}/api/admin/waivers/summary`, {
        credentials: 'include'
      });
      if (waiverResponse.ok) {
        const waiverData = await waiverResponse.json();
        setWaiverSummary(waiverData);
      }
      
    } catch (error) {
      console.error('Dashboard load error:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      await fetch(`${getApiBaseUrl()}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed');
    }
  };
  
  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const time = new Date();
    time.setHours(parseInt(hours), parseInt(minutes));
    return time.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="xl" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-24" data-testid="admin-dashboard">
      {/* Premium Header */}
      <div className="glass-effect sticky top-0 z-40">
        <div className="px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Command Center</p>
              <h1 className="text-2xl font-black uppercase tracking-tight">
                Admin Dashboard
              </h1>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="hover:bg-primary/10" data-testid="admin-settings-menu-btn">
                  <GearSix size={24} weight="duotone" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/change-password')} data-testid="admin-change-password-menu-item">
                  <LockKey size={18} className="mr-2" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-500" data-testid="admin-logout-menu-item">
                  <SignOut size={18} className="mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="px-6 py-8 space-y-8 stagger-children">
        {/* Premium Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="stats-card group">
            <div className="icon-container mb-4 text-primary">
              <Users size={24} weight="duotone" />
            </div>
            <p className="text-4xl font-black text-primary mb-1">{stats.total_players || 0}</p>
            <p className="text-sm text-muted-foreground uppercase tracking-wide">Total Players</p>
          </div>
          
          <div className="stats-card group">
            <div className="icon-container mb-4 text-blue-500">
              <Calendar size={24} weight="duotone" />
            </div>
            <p className="text-4xl font-black text-blue-500 mb-1">{stats.todays_sessions || 0}</p>
            <p className="text-sm text-muted-foreground uppercase tracking-wide">Today's Sessions</p>
          </div>
          
          <div className="stats-card group">
            <div className="icon-container mb-4 text-green-500">
              <CheckCircle size={24} weight="duotone" />
            </div>
            <p className="text-4xl font-black text-green-500 mb-1">{stats.todays_checkins || 0}</p>
            <p className="text-sm text-muted-foreground uppercase tracking-wide">Today's Check-ins</p>
          </div>
          
          <div className="stats-card group">
            <div className="icon-container mb-4 text-yellow-500">
              <CurrencyDollar size={24} weight="duotone" />
            </div>
            <p className="text-4xl font-black text-yellow-500 mb-1">${(stats.today_revenue || 0).toFixed(0)}</p>
            <p className="text-sm text-muted-foreground uppercase tracking-wide">Today's Revenue</p>
          </div>
        </div>
        
        {/* Quick Actions - Premium Grid */}
        <div>
          <h2 className="text-lg font-heading mb-4">QUICK ACTIONS</h2>
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-10 gap-3">
            <Button 
              variant="ghost" 
              className="h-20 flex flex-col items-center justify-center gap-2 bg-card border border-border hover:border-primary/30 hover:bg-primary/5 rounded-xl transition-all"
              onClick={() => navigate('/admin/sessions')}
            >
              <Calendar size={22} weight="duotone" className="text-blue-500" />
              <span className="text-xs font-medium">Sessions</span>
            </Button>
            <Button 
              variant="ghost" 
              className="h-20 flex flex-col items-center justify-center gap-2 bg-card border border-border hover:border-primary/30 hover:bg-primary/5 rounded-xl transition-all"
              onClick={() => navigate('/admin/players')}
              data-testid="quick-action-players"
            >
              <Users size={22} weight="duotone" className="text-primary" />
              <span className="text-xs font-medium">Players</span>
            </Button>
            <Button 
              variant="ghost" 
              className="h-20 flex flex-col items-center justify-center gap-2 bg-card border border-border hover:border-primary/30 hover:bg-primary/5 rounded-xl transition-all"
              onClick={() => navigate('/admin/templates')}
            >
              <Clock size={22} weight="duotone" className="text-purple-500" />
              <span className="text-xs font-medium">Templates</span>
            </Button>
            <Button 
              variant="ghost" 
              className="h-20 flex flex-col items-center justify-center gap-2 bg-card border border-border hover:border-primary/30 hover:bg-primary/5 rounded-xl transition-all"
              onClick={() => navigate('/admin/discount-codes')}
            >
              <CreditCard size={22} weight="duotone" className="text-pink-500" />
              <span className="text-xs font-medium">Discounts</span>
            </Button>
            <Button 
              variant="ghost" 
              className="h-20 flex flex-col items-center justify-center gap-2 bg-card border border-border hover:border-primary/30 hover:bg-primary/5 rounded-xl transition-all"
              onClick={() => navigate('/admin/locations')}
            >
              <MapPin size={22} weight="duotone" className="text-green-500" />
              <span className="text-xs font-medium">Locations</span>
            </Button>
            <Button 
              variant="ghost" 
              className="h-20 flex flex-col items-center justify-center gap-2 bg-card border border-border hover:border-cyan-500/30 hover:bg-cyan-500/5 rounded-xl transition-all"
              onClick={() => navigate('/admin/users')}
            >
              <UserGear size={22} weight="duotone" className="text-cyan-500" />
              <span className="text-xs font-medium">Staff</span>
            </Button>
            <Button 
              variant="ghost" 
              className="h-20 flex flex-col items-center justify-center gap-2 bg-card border border-border hover:border-green-500/30 hover:bg-green-500/5 rounded-xl transition-all"
              onClick={() => navigate('/scan')}
            >
              <CheckCircle size={22} weight="duotone" className="text-green-500" />
              <span className="text-xs font-medium">Check-in</span>
            </Button>
            <Button 
              variant="ghost" 
              className="h-20 flex flex-col items-center justify-center gap-2 bg-card border border-border hover:border-primary/30 hover:bg-primary/5 rounded-xl transition-all"
              onClick={() => navigate('/admin/settings')}
            >
              <Gear size={22} weight="duotone" className="text-gray-400" />
              <span className="text-xs font-medium">Settings</span>
            </Button>
            <Button 
              variant="ghost" 
              className="h-20 flex flex-col items-center justify-center gap-2 bg-card border border-border hover:border-yellow-500/30 hover:bg-yellow-500/5 rounded-xl transition-all"
              onClick={() => navigate('/admin/financials')}
            >
              <CurrencyDollar size={22} weight="duotone" className="text-yellow-500" />
              <span className="text-xs font-medium">Financials</span>
            </Button>
          </div>
          
          {/* Second Row */}
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-10 gap-3 mt-3">
            <Button 
              variant="ghost" 
              className="h-20 flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/30 hover:border-primary/50 rounded-xl transition-all"
              onClick={() => navigate('/admin/analytics')}
              data-testid="quick-action-analytics"
            >
              <ChartLine size={22} weight="duotone" className="text-primary" />
              <span className="text-xs font-medium">Analytics</span>
            </Button>
            <Button 
              variant="ghost" 
              className="h-20 flex flex-col items-center justify-center gap-2 bg-card border border-border hover:border-emerald-500/30 hover:bg-emerald-500/5 rounded-xl transition-all"
              onClick={() => navigate('/admin/credits')}
              data-testid="quick-action-credits"
            >
              <CreditCard size={22} weight="duotone" className="text-emerald-500" />
              <span className="text-xs font-medium">Credits</span>
            </Button>
            <Button 
              variant="ghost" 
              className="h-20 flex flex-col items-center justify-center gap-2 bg-yellow-500/5 border border-yellow-500/30 hover:border-yellow-500/50 rounded-xl transition-all"
              onClick={() => navigate('/admin/refunds')}
              data-testid="quick-action-refunds"
            >
              <CurrencyDollar size={22} weight="duotone" className="text-yellow-500" />
              <span className="text-xs font-medium">Refunds</span>
            </Button>
            <Button 
              variant="ghost" 
              className="h-20 flex flex-col items-center justify-center gap-2 bg-card border border-border hover:border-indigo-500/30 hover:bg-indigo-500/5 rounded-xl transition-all"
              onClick={() => navigate('/admin/exports')}
              data-testid="quick-action-exports"
            >
              <Download size={22} weight="duotone" className="text-indigo-500" />
              <span className="text-xs font-medium">Exports</span>
            </Button>
            <Button 
              variant="ghost" 
              className="h-20 flex flex-col items-center justify-center gap-2 bg-card border border-border hover:border-sky-500/30 hover:bg-sky-500/5 rounded-xl transition-all"
              onClick={() => navigate('/admin/parents')}
              data-testid="quick-action-parents"
            >
              <UserList size={22} weight="duotone" className="text-sky-500" />
              <span className="text-xs font-medium">Parents</span>
            </Button>
            <Button 
              variant="ghost" 
              className="h-20 flex flex-col items-center justify-center gap-2 bg-red-500/5 border border-red-500/30 hover:border-red-500/50 rounded-xl transition-all"
              onClick={() => navigate('/staff/resources')}
              data-testid="quick-action-eap"
            >
              <FirstAid size={22} weight="duotone" className="text-red-500" />
              <span className="text-xs font-medium">EAP/Staff</span>
            </Button>
            <Button 
              variant="ghost" 
              className="h-20 flex flex-col items-center justify-center gap-2 bg-card border border-border hover:border-primary/30 hover:bg-primary/5 rounded-xl transition-all"
              onClick={() => navigate('/rules')}
              data-testid="quick-action-rules"
            >
              <Scroll size={22} weight="duotone" className="text-primary" />
              <span className="text-xs font-medium">Rules</span>
            </Button>
            <Button 
              variant="ghost" 
              className="h-20 flex flex-col items-center justify-center gap-2 bg-red-500/5 border border-red-500/30 hover:border-red-500/50 rounded-xl transition-all"
              onClick={handleCleanupDemoData}
              disabled={cleaningUp}
              data-testid="quick-action-cleanup"
            >
              <Trash size={22} weight="duotone" className="text-red-500" />
              <span className="text-xs font-medium">{cleaningUp ? 'Cleaning...' : 'Cleanup'}</span>
            </Button>
            <Button 
              variant="ghost" 
              className="h-20 flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/30 hover:border-orange-500/50 rounded-xl transition-all"
              onClick={() => navigate('/open-run')}
              data-testid="quick-action-game-tracker"
            >
              <Basketball size={22} weight="duotone" className="text-orange-500" />
              <span className="text-xs font-medium">Game Tracker</span>
            </Button>
          </div>
        </div>
        
        {/* Alerts Section */}
        {alerts.length > 0 && (
          <Card className="bg-card border-border border-l-4 border-l-yellow-500">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Bell size={20} className="text-yellow-500" weight="duotone" />
                ALERTS ({alerts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {alerts.slice(0, 5).map((alert, index) => (
                  <div 
                    key={index}
                    className={`p-3 rounded-lg flex items-start gap-3 ${
                      alert.severity === 'warning' ? 'bg-yellow-500/10' : 'bg-blue-500/10'
                    }`}
                  >
                    <Warning 
                      size={18} 
                      className={alert.severity === 'warning' ? 'text-yellow-500' : 'text-blue-500'} 
                      weight="duotone" 
                    />
                    <span className="text-sm">{alert.message}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Analytics Summary */}
        {attendanceData && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <ChartLine size={20} className="text-primary" weight="duotone" />
                30-DAY ANALYTICS
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-muted/20 rounded-lg">
                  <p className="text-2xl font-bold text-primary">{attendanceData.summary?.total_sessions || 0}</p>
                  <p className="text-xs text-muted-foreground">Sessions</p>
                </div>
                <div className="text-center p-3 bg-muted/20 rounded-lg">
                  <p className="text-2xl font-bold text-blue-500">{attendanceData.summary?.total_registered || 0}</p>
                  <p className="text-xs text-muted-foreground">Registrations</p>
                </div>
                <div className="text-center p-3 bg-muted/20 rounded-lg">
                  <p className="text-2xl font-bold text-green-500">{attendanceData.summary?.avg_attendance_rate || 0}%</p>
                  <p className="text-xs text-muted-foreground">Attendance Rate</p>
                </div>
                <div className="text-center p-3 bg-muted/20 rounded-lg">
                  <p className="text-2xl font-bold text-yellow-500">{attendanceData.summary?.avg_fill_rate || 0}%</p>
                  <p className="text-xs text-muted-foreground">Fill Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Financial Overview - Admin Only */}
        {user?.role === 'admin' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <TrendUp size={20} className="text-primary" weight="duotone" />
                  FINANCIAL OVERVIEW
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Total Revenue</span>
                    <span className="font-bold text-primary">${(stats.total_revenue || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">This Month</span>
                    <span className="font-bold text-green-500">${(stats.month_revenue || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Pending Payments</span>
                    <span className="font-bold text-yellow-500">{stats.pending_payments || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-border lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <CreditCard size={20} className="text-primary" weight="duotone" />
                  RECENT TRANSACTIONS
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentTransactions.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No recent transactions</p>
                ) : (
                  <div className="space-y-3">
                    {recentTransactions.map((transaction) => (
                      <div key={transaction.payment_id} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-sm">
                            {transaction.player?.name || 'Unknown Player'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {transaction.session?.date} at {transaction.session?.location?.name}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-primary">${transaction.amount.toFixed(2)}</p>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            transaction.payment_status === 'paid' 
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {transaction.payment_status.toUpperCase()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Waiver Status Overview - Admin Only */}
        {(user?.role === 'admin' || userRole === 'admin') && (
          <Card className="bg-card border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <ShieldCheck size={20} className="text-primary" weight="duotone" />
                  WAIVER & CONSENT STATUS
                </CardTitle>
                <Badge variant="outline" className="text-xs">
                  {waiverSummary.players?.length || 0} Players
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                  <p className="text-3xl font-bold text-green-500">{waiverSummary.summary?.signed || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Active Waivers</p>
                </div>
                <div className="text-center p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                  <p className="text-3xl font-bold text-yellow-500">{waiverSummary.summary?.expired || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Expired</p>
                </div>
                <div className="text-center p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                  <p className="text-3xl font-bold text-red-500">{waiverSummary.summary?.missing || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Missing</p>
                </div>
              </div>
              
              {/* Player List with Waiver Status */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                <p className="text-sm font-medium text-muted-foreground mb-2">Player Waiver Details:</p>
                {waiverSummary.players?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">No players registered</p>
                ) : (
                  waiverSummary.players?.map((player) => (
                    <div 
                      key={player.player_id}
                      className="flex items-center justify-between p-3 bg-muted/20 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${
                          player.status === 'signed' ? 'bg-green-500' :
                          player.status === 'expired' ? 'bg-yellow-500' : 'bg-red-500'
                        }`} />
                        <div>
                          <p className="font-medium text-sm">{player.player_name}</p>
                          <p className="text-xs text-muted-foreground">Age: {player.age}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          player.status === 'signed' ? 'bg-green-500/20 text-green-400' :
                          player.status === 'expired' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {player.status === 'signed' ? 'ACTIVE' : 
                           player.status === 'expired' ? 'EXPIRED' : 'MISSING'}
                        </span>
                        {player.status === 'signed' && player.expires_at && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Expires: {new Date(player.expires_at).toLocaleDateString()}
                          </p>
                        )}
                        {player.signed_name && (
                          <p className="text-xs text-muted-foreground">
                            Signed by: {player.signed_name}
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* COPPA Notice */}
              <div className="mt-4 p-3 bg-primary/5 rounded-lg border border-primary/20">
                <p className="text-xs text-muted-foreground flex items-center gap-2">
                  <FileText size={14} className="text-primary" />
                  Waivers include COPPA-compliant parental consent. Signed waivers cannot be edited.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Quick Actions */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading">QUICK ACTIONS</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <Button 
              onClick={() => navigate('/admin/sessions')}
              className="h-20 flex flex-col items-center justify-center space-y-2 bg-primary/10 hover:bg-primary/20 text-primary border-primary/20"
              variant="outline"
              data-testid="manage-sessions-btn"
            >
              <Calendar size={24} weight="duotone" />
              <span className="text-sm font-medium">Manage Sessions</span>
            </Button>
            
            <Button 
              onClick={() => navigate('/scan')}
              className="h-20 flex flex-col items-center justify-center space-y-2 bg-green-500/10 hover:bg-green-500/20 text-green-500 border-green-500/20"
              variant="outline"
              data-testid="qr-scanner-btn"
            >
              <CheckCircle size={24} weight="duotone" />
              <span className="text-sm font-medium">QR Scanner</span>
            </Button>
          </CardContent>
        </Card>
        
        {/* Today's Sessions */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading">TODAY&apos;S SESSIONS</CardTitle>
          </CardHeader>
          <CardContent>
            {todaySessions.length === 0 ? (
              <div className="text-center py-8">
                <Calendar size={48} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No sessions scheduled for today</p>
              </div>
            ) : (
              <div className="space-y-4">
                {todaySessions.map((session) => (
                  <div key={session.session_id} className="flex items-center justify-between p-4 bg-muted/20 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="font-medium">{formatTime(session.start_time)} - {formatTime(session.end_time)}</p>
                          <p className="text-sm text-muted-foreground">{session.location?.name}</p>
                          {session.court && (
                            <p className="text-xs text-muted-foreground">Court: {session.court}</p>
                          )}
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-primary">{session.registered_count}</p>
                          <p className="text-xs text-muted-foreground">registered</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-green-500">{session.checked_in_count}</p>
                          <p className="text-xs text-muted-foreground">checked in</p>
                        </div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/scan?session=${session.session_id}`)}
                      data-testid={`session-checkin-${session.session_id}`}
                    >
                      Check-in
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      <MobileNavigation userRole={userRole} />
    </div>
  );
}

function StatsCard({ icon, title, value, subtitle, color = "text-primary" }) {
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4">
        <div className="flex items-center space-x-3">
          <div className={color}>{icon}</div>
          <div className="flex-1">
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs text-muted-foreground">{title}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}